import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextConsignment } from './Consignment';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ApiDigitalAdminConsignment } from '../../../../services/admin/consignment.service';
import { Error } from '../../../../models/error.model';
import { ConsignmentReport, ConsignmentReportLine } from '../../../../models/consignment.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatConsignmentProvider } from '../../../../scripts/consignment.format.script';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppPreloader from '../../../../components/Preloader';

export interface ConsignmentReportPageProps {};

let errorResponse: Error;

const ConsignmentReportPage: React.FunctionComponent<ConsignmentReportPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextConsignment()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [report, setReport] = useState<ConsignmentReport | null>(null)

  const loadReport = async (id_company: string, id_consignment: string) => {
    await ApiDigitalAdminConsignment.getConsignmentReport(id_company, id_consignment).then( (response) => {
      if (response.status === 200) {
        setReport(response.data)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitGenerateInvoice = async () => {
    if (companyForUser && param.id) {
      GlobalSwal.fire({
        title: lang.labels.generateProviderInvoice,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesGenerate,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ApiDigitalAdminConsignment.postConsignmentInvoice(companyForUser.id, param.id!).then( (response) => {
            if (response.status === 201) {
              let invoiceData = response.data

              GlobalSwal.fire({
                title: lang.labels.successfullyCreated,
                text: lang.labels.actionCompletedRedirectingToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                if (invoiceData?.id) {
                  navigate('/main/purchase/invoice/view/' + invoiceData.id)
                }
              })
            } else {
              errorResponse = response.data
              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadReport(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.consignments, branch: lang.labels.generateReport}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && report
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h2>{lang.labels.generateReport}</h2>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex flex-wrap mb-5">
                <div className="flex-equal me-5">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.provider}:</td>
                        <td className="min-w-200px">{formatConsignmentProvider(report.provider) || report.provider?.name || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex-equal">
                  <table className="table align-middle gy-1 m-0">
                    <tbody className="fw-bold">
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.startDate}:</td>
                        <td className="min-w-200px">{report.period?.start_date ? dateFormat(lang.code, report.period.start_date) : '-'}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-muted">{lang.labels.endDate}:</td>
                        <td className="min-w-200px">{report.period?.end_date ? dateFormat(lang.code, report.period.end_date) : '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              { report.lines && report.lines.length > 0 &&
                <div className="table-responsive mb-5">
                  <table className="table align-middle table-row-dashed mb-0">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="min-w-150px">{lang.labels.product}</th>
                        <th className="w-80px text-end">{lang.labels.quantityReceived}</th>
                        <th className="w-80px text-end">{lang.labels.quantitySold}</th>
                        <th className="w-80px text-end">{lang.labels.quantityReturned}</th>
                        <th className="w-80px text-end">{lang.labels.quantityAvailable}</th>
                        <th className="w-120px text-end">{lang.labels.totalSoldValue}</th>
                        <th className="w-120px text-end">{lang.labels.commissionAmount}</th>
                        <th className="w-120px text-end">{lang.labels.amountDueToProvider}</th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      { report.lines.map((line: ConsignmentReportLine, index: number) => (
                        <tr key={index}>
                          <td>{line.product_name}</td>
                          <td className="text-end">{line.quantity_received}</td>
                          <td className="text-end">{line.quantity_sold}</td>
                          <td className="text-end">{line.quantity_returned}</td>
                          <td className="text-end">{line.quantity_remaining}</td>
                          <td className="text-end">{formatMoney(line.total_sold_value)}</td>
                          <td className="text-end">{formatMoney(line.commission_amount)}</td>
                          <td className="text-end">{formatMoney(line.amount_due_to_provider)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              }
              { report.totals &&
                <div className="border-top pt-5">
                  <div className="d-flex flex-stack px-5 py-2">
                    <div className="fw-bold pe-20">{lang.labels.totalSoldValue}:</div>
                    <div className="text-end fw-bolder ps-20">{formatMoney(report.totals.total_sold_value)}</div>
                  </div>
                  <div className="d-flex flex-stack px-5 py-2">
                    <div className="fw-bold pe-20">{lang.labels.commissionAmount}:</div>
                    <div className="text-end fw-bolder ps-20">{formatMoney(report.totals.total_commission)}</div>
                  </div>
                  <div className="d-flex flex-stack px-5 py-2 bg-light-success rounded">
                    <div className="fw-bolder pe-20 fs-5">{lang.labels.amountDueToProvider}:</div>
                    <div className="text-end fw-bolder ps-20 fs-5">{formatMoney(report.totals.total_due_to_provider)}</div>
                  </div>
                </div>
              }
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <button className="btn btn-lg btn-primary fw-bolder" type="button" onClick={submitGenerateInvoice}>
              {lang.labels.generateProviderInvoice}
            </button>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ConsignmentReportPage;
